/* eslint-disable camelcase */
import { FC, useRef, useState } from 'react';
import { translate } from 'translations';
import { AdminSearch, Icon, SelectTimeFrame, Button, Tooltip } from 'learning-panel-components';
import { MRT_Cell, MRT_Row, MRT_TableInstance, MRT_TableOptions } from 'material-react-table';
import Table from '@components/adminPanel/table/table';
import { IconButton, Menu, useMediaQuery } from '@mui/material';
import { getLocaleDateTimeString, mixpanelTrack, gainsightTrack } from 'utils';
import { Report, ReportStatus } from 'network-utils';
import Theme from '../../theme';
import styles from './downloadhubExtended.module.scss';
import MenuItemWrapper from './menuItem/MenuItem';
import GreenCheck from '../utils/greenCheck';
import UndoPopup from '../utils/undoPopup';

const DownloadhubExtended: FC<DownloadhubExtendedProps> = (props: DownloadhubExtendedProps) => {
    const {
        data,
        onSearch,
        totalRows,
        isFetching,
        isLoading,
        fetchNextPage,
        searchString,
        setSelectedIds,
        onDownload,
        onDelete,
        selectTimeFrameRef,
        menuRef,
        handleViewed,
        onRegenerate,
        setSort,
        handleDateFilter,
        undoData,
        handleRename,
        greenCheckUuids,
        handleGreenCheckOff
    } = props;
    const matchesBigScreen = useMediaQuery('(min-height: 900px)');
    const matchesSmallScreen = useMediaQuery('(min-height: 800px)');
    const matchesTinySmallScreen = useMediaQuery('(min-height: 350px)');

    const renameText = translate('Rename');
    const deleteText = translate('Delete');
    const failedToGenerateReport = translate('failedToGenerateReport');
    const InProgressReport = translate('InProgressReport');
    const failedToDeleteReport = translate('failedToDeleteReport');
    const exportedOnText = translate('ExportedOn');
    const sourceText = translate('Source');
    const reportText = translate('Report');
    const anchorRef = useRef<HTMLButtonElement>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [editedReports, setEditReports] = useState<Record<string, Report>>({});
    const [selectedRowActionMenu, setSelectedRowActionMenu] = useState<MRT_Row<any> | undefined>();
    const [selectedRowUuidStatus, setSelectedRowUuidStatus] = useState<Record<string, string>>({});

    const calcHeightSize = () => {
        if (matchesBigScreen) {
            return '45rem';
        }
        if (matchesSmallScreen) {
            return '38rem';
        }
        if (matchesTinySmallScreen) {
            return '20rem';
        }
        return '5rem';
    };

    const isNew = (report: Report) => {
        return !report.isViewed && report.status == ReportStatus.COMPLETED;
    };

    const renderStatus = (status: ReportStatus) => {
        const getStatusContent = (title: string, iconName: string | undefined, content: React.ReactNode) => (
            <Tooltip type="secondary" title={title} placement="top" titleClassName={styles['tooltip-title']}>
                <span>
                    {iconName && <Icon name={iconName} className={styles.icon} />}
                    {content}
                </span>
            </Tooltip>
        );

        return (
            <div className={styles.status}>
                {status === ReportStatus.ERROR && getStatusContent(failedToGenerateReport, 'errorIconAdmin', null)}
                {status === ReportStatus.FAILED_TO_DELETE &&
                    getStatusContent(failedToDeleteReport, 'failedToDelete', null)}
                {status === ReportStatus.IN_PROGRESS &&
                    getStatusContent(InProgressReport, undefined, <div className={styles.loader}></div>)}
            </div>
        );
    };

    const renderReportCell = (isNew: boolean, title: string, uuid: string, table: MRT_TableInstance<any>) => {
        const tableBoundingRect = table.refs.tableContainerRef.current.getBoundingClientRect();
        const undoPopupContainerStyle = {
            top: `${tableBoundingRect.y + tableBoundingRect.height}px`,
            position: 'fixed',
            left: `${tableBoundingRect.x}px`,
            bottom: 'auto'
        };
        return (
            <div className={styles['report-cell-wrapper']}>
                {isNew && (
                    <div className={styles['new-wrapper']}>
                        <Icon name="newIcon" className={styles.icon} style={{ display: 'flex' }} />
                        <div className={styles.text}>{translate('New')}</div>
                    </div>
                )}
                <div className={styles['report-title']}>{title}</div>
                {Object.keys(undoData).includes(uuid) && (
                    <UndoPopup undoData={undoData} uuid={uuid} containerStyle={undoPopupContainerStyle} />
                )}
            </div>
        );
    };

    const renderExportedOn = (exportDate: string) => {
        return (
            <div className={styles['export-wrapper']}>
                <div className={styles.date}>{getLocaleDateTimeString(exportDate, 'en')}</div>
            </div>
        );
    };

    const renderSourceCell = (source: string) => {
        return (
            <div className={styles['source-wrapper']}>
                <div className={styles.source}>{source}</div>
            </div>
        );
    };

    const renderReportHeader = (column: any) => {
        return (
            <div className={styles['header-wrapper']}>
                <div className={styles['header-title']}>{column.columnDef.header}</div>
                <Icon name="sortIcon" className={styles.icon} onClick={setSort} style={{ display: 'flex' }} />
            </div>
        );
    };

    const renderExportenOnHeader = (column: any) => {
        return (
            <div className={styles['header-wrapper']}>
                <div className={styles['header-title']} style={{ marginRight: '1rem' }}>
                    {column.columnDef.header}
                </div>
            </div>
        );
    };

    const UseTimeframe = (closeMenu: any) => {
        const [selectedTimeFrame, setSelectedTimeFrame] = useState<{
            to?: Date;
            from?: Date;
            timeFrame?: any;
        }>({});
        {
            return [
                <div
                    key={1}
                    data-testid="filters-wrapper"
                    className={styles['filters-wrapper']}
                    ref={selectTimeFrameRef}
                >
                    <div className={styles['filters-container']}>
                        <SelectTimeFrame
                            key={1}
                            selectedTimeFrame={selectedTimeFrame}
                            onSelectTimeFrame={(timeFrame?: any, from?: Date, to?: Date) => {
                                setSelectedTimeFrame({ timeFrame, from, to });
                            }}
                            limitUntil={new Date()}
                        />
                    </div>
                    <div className={styles['actions-wrapper']}>
                        <Button
                            type="learningPanelAdminCancel"
                            text={translate('Cancel')}
                            onClick={() => closeMenu()}
                            lowerCase={true}
                        />
                        <Button
                            type="learningPanelAdminActionButton"
                            text={translate('Apply')}
                            onClick={() => {
                                handleDateFilter(selectedTimeFrame);
                                closeMenu();
                            }}
                            lowerCase={true}
                        />
                    </div>
                </div>
            ];
        }
    };

    const onReportNameChange = (e: any, row: any) => {
        setEditReports({ ...editedReports, [row.id]: e.target.value ?? row.original.name });
    };

    const columns = [
        {
            accessorKey: 'report',
            header: reportText,
            size: 90,
            enableEditing: true,
            Cell: ({ row, table }: { row: MRT_Row<any>; table: MRT_TableInstance<any> }) =>
                renderReportCell(isNew(row.original), row.original.name, row.original.uuid, table),
            Header: ({ column }: any) => renderReportHeader(column),
            muiEditTextFieldProps: ({ cell, row }: { cell: any; row: any }) => ({
                type: 'text',
                value: editedReports[row.id] ?? row.original.name,
                disabled: false,
                sx: {
                    '& .MuiInputBase-root': {
                        fontFamily: 'Inter',
                        fontSize: '0.8125rem',
                        fontStyle: 'italic',
                        fontWeight: 600,
                        color: '#626262'
                    }
                },
                onBlur: (event: any) => {
                    if (!event.target.value) {
                        const updated = { ...editedReports };
                        delete updated[row.id];
                        setEditReports(updated);
                    } else {
                        handleRename(row.original.uuid, event.target.value, row.original.name);
                        onReportNameChange(event, row);
                    }
                },
                onChange: (event: any) => {
                    onReportNameChange(event, row);
                }
            })
        },
        {
            accessorKey: 'exportedOn',
            header: exportedOnText,
            Header: ({ column }: any) => renderExportenOnHeader(column),
            Cell: ({ row }: { row: MRT_Row<any> }) => renderExportedOn(row.original.createdAt),
            size: 70,
            enableColumnActions: true,
            renderColumnActionsMenuItems: ({ closeMenu }: any) => UseTimeframe(closeMenu),
            muiColumnActionsButtonProps: {
                children: <Icon name="calendarAdmin" className={styles.icon} />
            }
        },
        {
            id: 'source',
            header: sourceText,
            Cell: ({ row }: { row: MRT_Row<any> }) => renderSourceCell(row.original.source),
            size: 60
        }
    ];

    const handleOpenRowActionMenu = (
        event: React.MouseEvent<HTMLElement>,
        uuid: string,
        row: MRT_Row<any>,
        status: ReportStatus
    ) => {
        const relevantAnchor = event.currentTarget.parentElement?.parentElement?.parentElement?.parentElement;
        event.stopPropagation();
        event.preventDefault();
        setAnchorEl(relevantAnchor || event.currentTarget);
        setSelectedRowActionMenu(row);
        setSelectedRowUuidStatus({ ...selectedRowUuidStatus, [uuid]: status });
        handleViewed(uuid);
    };

    const renderStatusComponent = (status: ReportStatus, hideOnHover: boolean, uuid: string) => (
        <div className={hideOnHover ? 'not-display-hover-table-actions-wrapper' : ''}>
            {renderStatus(status)}
            <GreenCheck
                isOn={greenCheckUuids.includes(uuid)}
                isHidden={false}
                handleOff={() => handleGreenCheckOff(uuid)}
            />
        </div>
    );

    const editCell = (table: MRT_TableInstance<any>) => {
        const reportCell = selectedRowActionMenu?.getVisibleCells()[0];
        if (reportCell) {
            table.setEditingCell(reportCell);
        }
    };

    const deleteCell = (table: MRT_TableInstance<any>) => {
        if (selectedRowUuidStatus[selectedRowActionMenu?.original.uuid]) {
            onDelete(selectedRowActionMenu?.original.uuid, selectedRowUuidStatus[selectedRowActionMenu?.original.uuid]);
            table.setEditingCell(null);
        }
    };

    const renderKebabMenu = (uuid: string, status: ReportStatus, table: MRT_TableInstance<any>, row: MRT_Row<any>) => {
        return (
            <div>
                <IconButton
                    onClick={e => handleOpenRowActionMenu(e, uuid, row, status)}
                    ref={anchorRef}
                    sx={{ padding: '0 !important' }}
                >
                    <Icon name="kebab" className={styles['icon-container']} />
                </IconButton>
                <Menu
                    ref={menuRef}
                    anchorEl={anchorEl}
                    onClick={event => event.stopPropagation()}
                    onClose={() => setAnchorEl(null)}
                    open={!!anchorEl}
                >
                    <MenuItemWrapper
                        itemKey={'1'}
                        text={renameText}
                        icon={'EditIconAdmin'}
                        onClick={() => {
                            editCell(table);
                            mixpanelTrack({
                                eventName: 'Download Hub Action',
                                properties: {
                                    'Hub Type': 'Expanded',
                                    Action: 'Rename'
                                }
                            });
                            gainsightTrack('Download Hub Action', {
                                'Hub Type': 'Expanded',
                                Action: 'Rename'
                            });
                        }}
                        onClose={() => setAnchorEl(null)}
                    />
                    <MenuItemWrapper
                        itemKey={'2'}
                        text={deleteText}
                        icon={'TrashIconAdmin'}
                        onClick={() => {
                            deleteCell(table);
                            mixpanelTrack({
                                eventName: 'Download Hub Action',
                                properties: {
                                    'Hub Type': 'Expanded',
                                    Action: 'Delete'
                                }
                            });
                            gainsightTrack('Download Hub Action', {
                                'Hub Type': 'Expanded',
                                Action: 'Delete'
                            });
                        }}
                        showAnswerOptions={true}
                        onClose={() => setAnchorEl(null)}
                    />
                </Menu>
            </div>
        );
    };

    const rowActionsMenu = (row: MRT_Row<any>, table: MRT_TableInstance<any>, cell: MRT_Cell<any>) => {
        const report = row.original;
        return (
            <div className={styles.wrapper}>
                {renderStatusComponent(report.status, true, report.uuid)}
                <div className="table-actions-wrapper">
                    <div className={styles.action}>
                        <div className={styles['actions-wrapper']}>
                            {renderStatusComponent(report.status, false, report.uuid)}
                            {report.status === ReportStatus.ERROR && (
                                <Icon
                                    name="retry"
                                    className={styles['icon-container']}
                                    onClick={() => onRegenerate(report.uuid)}
                                />
                            )}

                            {report.status === ReportStatus.COMPLETED && (
                                <Icon
                                    name="downloadIconAdmin"
                                    className={styles['icon-container']}
                                    onClick={() => {
                                        handleViewed(report.uuid);
                                        onDownload(report.uuid, report.source);
                                        mixpanelTrack({
                                            eventName: 'Download Hub Action',
                                            properties: {
                                                'Hub Type': 'Expanded',
                                                Action: 'Download'
                                            }
                                        });
                                        gainsightTrack('Download Hub Action', {
                                            'Hub Type': 'Expanded',
                                            Action: 'Download'
                                        });
                                    }}
                                />
                            )}
                        </div>
                        {report.status !== ReportStatus.IN_PROGRESS &&
                            renderKebabMenu(report.uuid, report.status, table, row)}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles['download-hub-wrapper']}>
            <div className={styles['title-container']}>
                <div className={styles['title-wrapper']}>
                    <div className={styles.title}>{translate('DownloadHub')}</div>
                    <div className={styles['sub-title']}> {translate('GeneratedReports')}</div>
                </div>
            </div>
            <div className={styles.body}>
                <div className={styles.search}>
                    <div>
                        <AdminSearch
                            placeholder={'Search for a report name'}
                            onSearchChange={(search: string) => onSearch(search)}
                        />
                    </div>
                </div>
                <div style={{ width: '100%', position: 'relative' }}>
                    <Theme>
                        <>
                            <Table
                                data={data}
                                isSelected={false}
                                totalRows={totalRows}
                                isLoading={isLoading}
                                isFetching={isFetching}
                                columnsDeclaration={columns}
                                getRowId={(row: any) => row.uuid}
                                maxHeight={calcHeightSize()}
                                onSelectedChange={setSelectedIds}
                                fetchMethod={'infinity'}
                                fetchNextPage={fetchNextPage}
                                totalFetched={data.length}
                                searchString={searchString}
                                rowHeight="3.125rem"
                                enableRowAction
                                backgroundColor={
                                    'linear-gradient(83deg, rgba(226, 216, 247, 0.40) 2.82%, rgba(242, 125, 177, 0.05) 52.7%, rgba(226, 216, 247, 0.40) 94.71%)'
                                }
                                renderRowAction={(
                                    row: MRT_Row<any>,
                                    table: MRT_TableInstance<any>,
                                    cell: MRT_Cell<any>
                                ) => {
                                    return rowActionsMenu(row, table, cell);
                                }}
                                dependencies={[editedReports, undoData]}
                            />
                        </>
                    </Theme>
                </div>
            </div>
        </div>
    );
};

interface DownloadhubExtendedProps {
    data: Report[];
    onSearch: Function;
    totalRows: number;
    isLoading: boolean;
    isFetching: boolean;
    fetchNextPage: Function;
    searchString: string;
    setSelectedIds: Function;
    onDownload: Function;
    handleDateFilter: Function;
    onRegenerate: Function;
    onDelete: Function;
    setSort: Function;
    selectTimeFrameRef: any;
    menuRef: any;
    handleViewed: Function;
    undoData: any;
    handleRename: Function;
    greenCheckUuids: string[];
    handleGreenCheckOff: Function;
}

export default DownloadhubExtended;
